/**
 * Futures screener (owner 2026-09-27; mockup 02's "Crypto screener" made a futures screener):
 * Gainers / Losers / Movers over a symbol universe. Pure ranking so the sort order is tested.
 *
 * Reference price: the session **settlement** once the gateway streams `SessionStats`
 * (docs/PLAN.md TODO), until then the **first print this window received** — rows say which, so
 * a "since subscribed" change is never mistaken for a daily change. All price math is in ticks.
 */

export type ScreenerInput = {
  symbol: string;
  /** Resolved contract, when known. */
  contract: string | null;
  lastTicks: number | null;
  /** Settlement (SessionStats) when available. */
  settlementTicks: number | null;
  /** First print since this window subscribed. */
  firstTicks: number | null;
  /** Contracts traded: session volume (SessionStats) or since subscribed. */
  volume: number;
  trades: number;
  /** Price of one tick (tickNum / tickDen), to turn ticks into a percentage. */
  tickPrice: number | null;
};

export type ScreenerRow = ScreenerInput & {
  /** Which reference the change is measured from. */
  ref: "settlement" | "first" | null;
  refTicks: number | null;
  changeTicks: number | null;
  changePct: number | null;
};

export function screenerRow(i: ScreenerInput): ScreenerRow {
  const ref = i.settlementTicks !== null ? "settlement" : i.firstTicks !== null ? "first" : null;
  const refTicks = i.settlementTicks ?? i.firstTicks;
  const changeTicks = i.lastTicks !== null && refTicks !== null ? i.lastTicks - refTicks : null;
  const refPrice = refTicks !== null && i.tickPrice !== null ? refTicks * i.tickPrice : 0;
  const changePct =
    changeTicks !== null && i.tickPrice !== null && refPrice !== 0
      ? ((changeTicks * i.tickPrice) / refPrice) * 100
      : null;
  return { ...i, ref, refTicks, changeTicks, changePct };
}

export type ScreenerSortKey = "changePct" | "volume" | "symbol" | "trades";

/**
 * Stable ranking: by key (desc for numbers, asc for symbol), unknown values last, ties by volume
 * desc then symbol asc.
 */
export function sortRows(
  rows: readonly ScreenerRow[],
  key: ScreenerSortKey,
  desc = key !== "symbol",
): ScreenerRow[] {
  const val = (r: ScreenerRow): number | null =>
    key === "changePct" ? r.changePct : key === "volume" ? r.volume : key === "trades" ? r.trades : null;
  return [...rows].sort((a, b) => {
    if (key === "symbol") return desc ? b.symbol.localeCompare(a.symbol) : a.symbol.localeCompare(b.symbol);
    const va = val(a);
    const vb = val(b);
    if (va === null && vb !== null) return 1;
    if (vb === null && va !== null) return -1;
    if (va !== null && vb !== null && va !== vb) return desc ? vb - va : va - vb;
    if (a.volume !== b.volume) return b.volume - a.volume;
    return a.symbol.localeCompare(b.symbol);
  });
}

/** Gainers (change > 0, biggest first) and losers (change < 0, biggest drop first), n each. */
export function movers(
  rows: readonly ScreenerRow[],
  n: number,
): { gainers: ScreenerRow[]; losers: ScreenerRow[] } {
  const gainers = sortRows(
    rows.filter((r) => r.changePct !== null && r.changePct > 0),
    "changePct",
    true,
  ).slice(0, n);
  const losers = sortRows(
    rows.filter((r) => r.changePct !== null && r.changePct < 0),
    "changePct",
    false,
  ).slice(0, n);
  return { gainers, losers };
}

/** Rows whose volume (contracts) is at least `min`. */
export function minVolume(rows: readonly ScreenerRow[], min: number): ScreenerRow[] {
  return min > 0 ? rows.filter((r) => r.volume >= min) : [...rows];
}
